import ApolloClient, { ApolloError } from 'apollo-client'
import React, { useContext, useState } from 'react'
import { Mutation, MutationFunction, withApollo, WithApolloClient } from 'react-apollo'

import { PageContext } from 'main/components/common/Page'
import { AppContext, IAppContext } from 'main/framework'
import { Button } from 'main/ui'
import { errorAlert, IPageAlert, successAlert } from 'main/util/page'

import {
  DELETE_PAYMENT_METHOD_PENDING_MUTATION,
  DeletePaymentMethodPending,
  DeletePaymentMethodPendingVariables,
  PAYMENT_METHODS_QUERY,
  PaymentMethods_paymentMethodPending as IPaymentMethodPending,
  PLAID_PUBLIC_TOKEN_QUERY,
  PlaidPublicToken,
  UPSERT_PAYMENT_METHOD_MUTATION,
  UpsertPaymentMethod,
  UpsertPaymentMethodVariables,
} from 'main/gql/paymentMethod'
import { Plaid } from 'main/util/paymentMethod'
import { logError } from 'main/util/paymentMethod/common'
import { ISuccessData } from 'main/util/paymentMethod/plaid'

interface IContainerProps {
  children: React.ReactNode
  paymentMethodPending: IPaymentMethodPending
}

type ContainerPropsInternal = WithApolloClient<IContainerProps>

function Container(props: ContainerPropsInternal) {
  const { paymentMethodPending, client, children } = props
  const [loading, setLoading] = useState<boolean>(false)

  const { user } = useContext(AppContext)
  const { setAlert } = useContext(PageContext)

  const refetchQueries = [{ query: PAYMENT_METHODS_QUERY, variables: { userId: user!.id } }]

  return (
    <Mutation<UpsertPaymentMethod, UpsertPaymentMethodVariables>
      mutation={UPSERT_PAYMENT_METHOD_MUTATION}
      onCompleted={() => setAlert(successAlert)}
      onError={(error: ApolloError) => handleApolloError(error, setAlert)}
      refetchQueries={refetchQueries}
    >
      {(upsertPaymentMethod, { loading: upsertingPaymentMethod }) => (
        <Mutation<DeletePaymentMethodPending, DeletePaymentMethodPendingVariables>
          mutation={DELETE_PAYMENT_METHOD_PENDING_MUTATION}
          onError={(error: ApolloError) => handleApolloError(error, setAlert)}
          refetchQueries={refetchQueries}
        >
          {(deletePaymentMethodPending, { loading: deletingPaymentMethodPending }) => (
            <VerifyButton
              user={user!}
              client={client}
              setAlert={setAlert}
              loading={upsertingPaymentMethod || deletingPaymentMethodPending || loading}
              deletePaymentMethodPending={deletePaymentMethodPending}
              upsertPaymentMethod={upsertPaymentMethod}
              paymentMethodPending={paymentMethodPending}
              onClick={() => setLoading(true)}
              onDone={() => setLoading(false)}
            >
              {children}
            </VerifyButton>
          )}
        </Mutation>
      )}
    </Mutation>
  )
}

interface IProps {
  user: IAppContext['user']
  client: ApolloClient<{}>
  children: React.ReactNode
  deletePaymentMethodPending: MutationFunction<
    DeletePaymentMethodPending,
    DeletePaymentMethodPendingVariables
  >
  upsertPaymentMethod: MutationFunction<UpsertPaymentMethod, UpsertPaymentMethodVariables>
  paymentMethodPending: IPaymentMethodPending
  loading: boolean
  setAlert: (alert: IPageAlert | null) => void
  onClick: () => void
  onDone: () => void
}

function VerifyButton(props: IProps) {
  const {
    user,
    client,
    children,
    deletePaymentMethodPending,
    upsertPaymentMethod,
    paymentMethodPending,
    loading,
    setAlert,
    onClick,
    onDone,
  } = props

  return <Button onClick={handleClick}>{loading ? 'Loading..' : children}</Button>

  async function verifyAndUpdatePaymentMethod() {
    try {
      onClick()

      const { data } = await client.query<PlaidPublicToken>({
        // Plaid expires public token after 30 mins, so do not cache
        // and always fetch a new one.
        fetchPolicy: 'network-only',
        query: PLAID_PUBLIC_TOKEN_QUERY,
        variables: {
          id: paymentMethodPending.id,
        },
      })

      // Pass in the public token for pending payment method as config
      // when initializing Plaid for manual verification.
      const { paymentMethodPendingPlaidPublicToken: token } = data

      const plaidData = await Plaid.create(user!, { token })

      // Manual verification successful, upsert a new payment method.
      const variables = formatVariables(plaidData)
      await upsertPaymentMethod({ variables })
      // @ts-ignore
    } catch (error: any) {
      // tslint:disable-next-line
      console.error(error)
      logError('Manual verification failed', { error })
      if (!error) return null

      // Manual verification failed and has been voided by Plaid,
      // so delete the pending payment method on our side too.
      if (Plaid.hasVoidedVerification(error)) {
        setAlert({
          body: "You've used up all of your attempts. Please try again by connecting a new account",
          kind: 'danger',
        })
        return deletePaymentMethodPending({ variables: { userId: user!.id } })
      }

      if ('graphQLErrors' in error) return handleApolloError(error, setAlert)
      setAlert({ body: error.display_message || error.error_message, kind: 'danger' })
    } finally {
      onDone()
    }
  }

  function handleClick() {
    verifyAndUpdatePaymentMethod()
  }
}

function formatVariables(plaidData: ISuccessData): UpsertPaymentMethodVariables {
  const { metadata, publicToken, stripeToken } = plaidData
  const accounts = metadata.accounts.map(account => ({
    id: account.id,
    mask: account.mask,
    name: account.name,
    subtype: account.subtype,
    type: account.type,
    verificationStatus: account.verification_status,
  }))

  const variables: UpsertPaymentMethodVariables = {
    publicToken: publicToken || null,
    metadata: {
      accounts,
      account: {
        id: metadata.account.id,
        mask: metadata.account.mask,
        name: metadata.account.name,
        subtype: metadata.account.subtype,
        type: metadata.account.type,
        verificationStatus: metadata.account.verification_status,
      },
      accountId: metadata.account_id,
      institution: metadata.institution,
      linkSessionId: metadata.link_session_id,
      publicToken: publicToken || null,
    },
  }
  if (stripeToken) {
    variables.stripeToken = stripeToken
  }
  return variables
}

function handleApolloError(error: ApolloError, setAlert: (alert: IPageAlert | null) => void) {
  const errorMessage = error.graphQLErrors[0] && error.graphQLErrors[0].message
  return errorMessage ? setAlert({ body: errorMessage, kind: 'danger' }) : setAlert(errorAlert)
}

export default withApollo<IContainerProps>(Container)
